import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, useStripe, CardElement, useElements } from '@stripe/react-stripe-js';

import {
  Button,
  CircularProgress,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Modal,
  Radio,
  RadioGroup,
  Typography,
  useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DoneIcon from '@mui/icons-material/Done';
import FileDownloadSharpIcon from '@mui/icons-material/FileDownloadSharp';

import { COLOR_GREY_400, COLOR_GREY_600, COLOR_PRIMARY_1, COLOR_PRIMARY_2 } from 'src/utils/colors';
import { COLOR_GREY, CountryCodeWithCurrency, CurrencyWithSubscriptionPrice, exchangeRates } from 'src/utils/const';
import {
  confirmCardPaymentRecursively,
  formatCurrencyBasedOnCountryCode,
} from 'src/utils/functions';
import { callApi } from 'src/core/callApi';
import useMediaQuery from '@mui/material/useMediaQuery';
import postOnSlack from 'src/core/postOnSlack';
import { logAmplitudeEvent } from 'src/utils/analytics';
import SenjaWidget from 'src/App/Dashboards/Market/Components/SenjaWidget';
import { useSetUserPaymentInterest } from 'src/hooks/useCustomHooks';
import { stripeConfig } from 'src/utils/stripeConfig';

const UPGRADE_SUBSCRIPTION_URL_TEST =
  'https://9s2r54r64l.execute-api.us-east-2.amazonaws.com/default/upgradeStripeSubscription';
const UPGRADE_SUBSCRIPTION_URL_PROD =
  'https://9s2r54r64l.execute-api.us-east-2.amazonaws.com/default/upgradeStripeSubscription';


const IS_TEST_MODE: boolean = false;

const expertFeatures = {
  expert: [
    '3 years historical data',
    'Interactive Map Insights ⭐',
    'Amenities and Performance Filters ⭐',
    'Draw Custom Markets ⭐',
    'Unlimited Airbnb Revenue Reports',
    'Unlimited Host Insights',    
  ],
  scale: [
    'Historical Data through 2021',
    'Advanced Listing Insight',
    'Export 1,000 Rows per Month',
    '100 Calculator & Market PDF Export/mo',
  ],
};

const FeatureList = ({ plan }) => {
  return (
    <List disablePadding={true} dense={true}>
      {expertFeatures[plan].map((feature, index) => (
        <ListItem key={index} disableGutters={true} dense={true}>
          <ListItemIcon sx={{ minWidth: '30px', marginRight: '0px' }}>
            <DoneIcon sx={{ color: 'green' }} />
          </ListItemIcon>
          {feature.includes('Money') ? (
            <ListItemText primary={<Typography sx={{ fontWeight: 500 }}>{feature}</Typography>} />
          ) : feature.includes('Download') ? (
            <ListItemText
              primary={
                <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center' }}>
                  {feature.replace('Download', '')}
                  <FileDownloadSharpIcon />
                </Typography>
              }
            />
          ) : (
            <ListItemText primary={feature} />
          )}
        </ListItem>
      ))}
    </List>
  );
};

const StripeContext = ({ plan, handleClose }) => {
  const theme = useTheme();
  const stripe = useStripe();
  const elements = useElements();
  const matches = useMediaQuery(theme.breakpoints.down(900));
  const userSignupCountryCode = useSelector((state: any) => state.dashboard.user.signupCountryCode);
  const userEmail = useSelector((state: any) => state.dashboard.user.email);
  const stripeAccount = useSelector((state: any) => state.dashboard.user.stripeAccount) || 'airbtics_uk';
  const search = useSelector((state: any) => state.dashboard.search);
  const userLocalCurrency = CountryCodeWithCurrency[userSignupCountryCode] || 'USD';
  const subscriptionPriceAccordingToUserLocalCurrency =
    CurrencyWithSubscriptionPrice[userLocalCurrency];
  const [duration, setDuration] = useState('yearly');
  const [proratedPrice, setProratedPrice] = useState<null | number>(null);
  const [subscriptionUpgradeLoading, setSubscriptionUpgradeLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [showNewCardForm, setShowNewCardForm] = useState(false);

  useEffect(() => {
    logAmplitudeEvent('payment_page_viewed', {
      plan_name: plan,
      plan_duration: duration,
      is_upgrade: true,
      view: '',
      visited_from: '',
    });
  }, [plan, duration]);

  const price =
    duration == 'monthly'
      ? formatCurrencyBasedOnCountryCode(
          parseFloat(subscriptionPriceAccordingToUserLocalCurrency[plan]['monthly'].toFixed(2)),
          userLocalCurrency
        )
      : formatCurrencyBasedOnCountryCode(
          parseFloat(
            (subscriptionPriceAccordingToUserLocalCurrency[plan][duration] / 12).toFixed()
          ),
          userLocalCurrency
        );

  const getProratedUpgradePrice = async () => {
    setProratedPrice(null);

    try {
      const url = IS_TEST_MODE ? UPGRADE_SUBSCRIPTION_URL_TEST : UPGRADE_SUBSCRIPTION_URL_PROD;
      const productID = IS_TEST_MODE
        ? stripeConfig[stripeAccount]?.testProductIDOptions?.[plan]?.productID?.[duration]
        : stripeConfig[stripeAccount]?.productIDOptions?.[plan]?.productID?.[duration];

      console.log('Product ID: ', productID, stripeAccount, duration, plan, IS_TEST_MODE, stripeConfig[stripeAccount]?.testProductIDOptions?.[plan]);

      const config = {
        queryParams: {
          newProductID: productID,
          newProductName: plan,
          newDuration: duration,
          operation: 'fetch_proration_price',
          ...(IS_TEST_MODE ? { isTest: true } : {}),
        },
      };
      const resp = await callApi(url, config, true);
      const prorationAmt = resp.proration_amount;
      if (prorationAmt && !isNaN(Number(prorationAmt))) {
        setProratedPrice(prorationAmt);
      } else {
        setProratedPrice(subscriptionPriceAccordingToUserLocalCurrency[plan][duration]);
      }
    } catch (err) {
      setProratedPrice(subscriptionPriceAccordingToUserLocalCurrency[plan][duration]);
    }
  };

  useEffect(() => {
    getProratedUpgradePrice();
  }, [duration]);

  const upgradeSubscription = async (paymentMethod: any | null = null) => {
    try {
      if (!stripe) {
        return;
      }
      setSubscriptionUpgradeLoading(true);
      const url = IS_TEST_MODE ? UPGRADE_SUBSCRIPTION_URL_TEST : UPGRADE_SUBSCRIPTION_URL_PROD;
      const productID = IS_TEST_MODE
        ? stripeConfig[stripeAccount]?.testProductIDOptions?.[plan]?.productID?.[duration]
        : stripeConfig[stripeAccount]?.productIDOptions?.[plan]?.productID?.[duration];
      const config = {
        queryParams: {
          newProductID: productID,
          newProductName: plan,
          newDuration: duration,
          operation: 'modify_subscription',
          ...(IS_TEST_MODE ? { isTest: true } : {}),
          payment_method_id: paymentMethod?.id || null,
        },
      };
      const resp = await callApi(url, config, true);
      setSubscriptionUpgradeLoading(false);
      if (resp.subscription_modification_status == 'success') {
        logAmplitudeEvent('subscribed', {
          plan_name: plan,
          plan_duration: duration,
          is_upgrade: true,
        });
        handleClose();
        setTimeout(() => {
          window.location.reload();
        }, 2000);
      } else if (resp.subscription_modification_status == 'requires_action') {
        // requires 3D auth
        const clientSecret = resp.client_secret;
        const ok = await confirmCardPaymentRecursively(stripe, clientSecret);
        if (ok) {
          console.log('3D auth success. Re-fetch payment intent from lambda');
          return upgradeSubscription();
        } else {
          postOnSlack(
            'tech',
            `3D Authentication failed for ${userEmail} during upgrade attempt to ${plan}-${duration}`
          );
          setErrorMessage('Payment authentication failed. Please try again.');
        }
      } else if (resp.subscription_modification_status == 'requires_new_payment_method' || resp.subscription_modification_status == 'failed') {
        setErrorMessage(
          resp.error_message || 'Payment failed. Please try again with a different card.'
        );
        setShowNewCardForm(true);
        postOnSlack(
          'tech',
          `Payment failed for ${userEmail} during upgrade attempt to ${plan}-${duration}. Error: ${resp.error_message}`
        );
      } else {
        setErrorMessage('An unexpected error occurred. Please try again.');
      }
    } catch (err) {
      setSubscriptionUpgradeLoading(false);
      setErrorMessage('An unexpected error occurred. Please try again.');
    }
  };

  const handleNewCardSubmit = async event => {
    event.preventDefault();
    if (!stripe || !elements) return;

    setSubscriptionUpgradeLoading(true);
    try {
      const cardElement = elements.getElement(CardElement);
      if (!cardElement) {
        setErrorMessage('Card element not found. Please try again.');
        setSubscriptionUpgradeLoading(false);
        return;
      }

      const { error, paymentMethod } = await stripe.createPaymentMethod({
        type: 'card',
        card: cardElement,
      } as any);

      if (error) {
        setErrorMessage(error.message || 'Payment failed. Please try again.');
        setSubscriptionUpgradeLoading(false);
        return;
      }

      // Call the upgrade subscription API with the new payment method
      upgradeSubscription(paymentMethod);
    } catch (err) {
      setSubscriptionUpgradeLoading(false);
      setErrorMessage('An unexpected error occurred. Please try again.');
    }
  };

  return (
    <Grid
      container
      direction="column"
      justifyContent="flex-start"
      alignItems="flex-start"
      sx={{
        background: 'white',
        width: matches ? '100vw' : '500px',
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        borderRadius: 4,
        outline: 'none !important',
        padding: 3,
        height: matches ? '100dvh' : '90vh',
        overflow: 'auto',
      }}
      key={`${plan}-${duration}-${userLocalCurrency}-upgrade-subscription-modal`}
    >
      <Grid container>
        <Grid
          item
          xs={12}
          container
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Typography
            variant="h5"
            sx={{ color: COLOR_PRIMARY_2, fontWeight: 500, textTransform: 'capitalize' }}
          >
            Upgrade to {plan} Plan
          </Typography>
          <CloseIcon
            sx={{ color: COLOR_GREY_400, cursor: 'pointer', fontWeight: 'bold' }}
            onClick={handleClose}
          />
        </Grid>
        {(() => {
          const planDescriptions = {
            expert: 'Designed for investors aiming to maximize ROI by understanding micro-neighborhood-level trends and demand.',
            scale: 'A tailored plan for property investors and growing businesses seeking comprehensive property-level data, advanced analytics, and export capabilities.',
          };
          const description = planDescriptions[plan];
          
          return description ? (
            <Grid item xs={12} sx={{ marginTop: '8px', marginBottom: '4px' }}>
              <Typography
                variant="body2"
                sx={{ 
                  color: COLOR_GREY_600,
                  fontStyle: 'italic',
                  lineHeight: 1.5,
                }}
              >
                {description}
              </Typography>
            </Grid>
          ) : null;
        })()}
        <Grid item xs={12} sx={{ margin: '10px 0', width: '100%' }}>
          <Divider />
        </Grid>
        <Grid item xs={12}>
          <FeatureList plan={plan} />
        </Grid>
        <Grid container item direction="row" justifyContent="flex-start" xs={12}>
          <Typography style={{ fontWeight: 'bold', marginTop: '20px' }}>Upgrade Summary</Typography>
        </Grid>
        <Grid
          item
          container
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          xs={12}
        >
          <Typography variant="body2">Plan</Typography>
          <FormControl>
            <RadioGroup
              row
              aria-labelledby="demo-row-radio-buttons-group-label"
              name="row-radio-buttons-group"
              value={duration}
              onChange={(event, value) => setDuration(value)}
            >
              <FormControlLabel value="yearly" control={<Radio />} label="Yearly" />
              <FormControlLabel value="monthly" control={<Radio />} label="Monthly" />
            </RadioGroup>
          </FormControl>
        </Grid>

        <Grid
          item
          container
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          xs={12}
        >
          <Typography variant="body2">New Price</Typography>
          <Typography variant="body2">
            {exchangeRates[userLocalCurrency].symbol}
            {price}{' '}
            <span
              style={{
                fontSize: '14px',
                color: COLOR_GREY_600,
                textDecoration: 'none !important',
              }}
            >
              /month
            </span>
          </Typography>
        </Grid>
        <Grid item xs={12} sx={{ margin: '10px 0', width: '100%' }}>
          <Divider />
        </Grid>
        <Grid
          item
          container
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          xs={12}
          sx={{ marginBottom: '10px' }}
        >
          <Typography variant="body2">
            Subtotal (per {duration == 'monthly' ? 'month' : 'year'})
          </Typography>
          <Typography variant="body2">
            {exchangeRates[userLocalCurrency].symbol}
            {formatCurrencyBasedOnCountryCode(
              parseFloat(subscriptionPriceAccordingToUserLocalCurrency[plan][duration].toFixed(2)),
              userLocalCurrency
            )}
          </Typography>
        </Grid>

        {errorMessage && (
          <Grid item xs={12} sx={{ mt: 2 }}>
            <Typography color="error" variant="body2" sx={{ textAlign: 'center' }}>
              {errorMessage}
            </Typography>
          </Grid>
        )}

        {!showNewCardForm ? (
          <>
            <Grid
              item
              container
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              xs={12}
            >
              <Typography variant="body2">Total Amount Due Today</Typography>
              {proratedPrice ? (
                <Typography variant="body2" sx={{ fontWeight: 'bold', color: COLOR_PRIMARY_2 }}>
                  {exchangeRates[userLocalCurrency].symbol}
                  {formatCurrencyBasedOnCountryCode(proratedPrice, userLocalCurrency)}
                </Typography>
              ) : (
                <CircularProgress />
              )}
            </Grid>
            <Grid
              item
              container
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              xs={12}
              sx={{ marginTop: '25px' }}
            >
              <Button
                sx={{
                  background: COLOR_PRIMARY_2,
                  color: 'white',
                  width: '100%',
                  ':hover': {
                    background: COLOR_PRIMARY_1,
                    color: 'white',
                  },
                }}
                onClick={upgradeSubscription}
                disabled={subscriptionUpgradeLoading}
              >
                Confirm Changes {subscriptionUpgradeLoading && <CircularProgress />}
              </Button>
            </Grid>
          </>
        ) : (
          <Grid
            item
            container
            direction="row"
            justifyContent="center"
            alignItems="center"
            xs={12}
            sx={{ mt: 2 }}
          >
            <Grid
              container
              item
              sx={{
                border: `2px solid ${COLOR_GREY}`,
                borderRadius: 10,
                padding: theme.spacing(2, 3, 2),
                boxShadow: theme.shadows[5],
              }}
            >
              <Typography variant="h6" sx={{ marginBottom: '10px' }}>
                Update Payment Method
              </Typography>
              <form onSubmit={handleNewCardSubmit} style={{ width: '100%' }}>
                <CardElement
                  options={{
                    style: {
                      base: {
                        fontSize: '16px',
                        color: '#424770',
                        '::placeholder': {
                          color: '#aab7c4',
                        },
                      },
                      invalid: {
                        color: '#9e2146',
                      },
                    },
                  }}
                />
                <Button
                  type="submit"
                  sx={{
                    background: COLOR_PRIMARY_2,
                    color: 'white',
                    width: '100%',
                    mt: 2,
                    ':hover': {
                      background: COLOR_PRIMARY_1,
                      color: 'white',
                    },
                  }}
                  disabled={subscriptionUpgradeLoading}
                >
                  Confirm Changes {subscriptionUpgradeLoading && <CircularProgress />}
                </Button>
              </form>
            </Grid>
          </Grid>
        )}

        {/* Reviews */}
        <SenjaWidget isMobile={true} />
      </Grid>
    </Grid>
  );
};

const UpgradeSubscriptionModal = ({ plan, setPlan, resetSubscriptionModal }) => {
  const stripeAccount = useSelector((state: any) => state.dashboard.user.stripeAccount) || 'airbtics_uk';
  const stripePromiseKey = IS_TEST_MODE
    ? stripeConfig[stripeAccount]?.promiseKeyTest
    : stripeConfig[stripeAccount]?.promiseKey;
  const stripePromise = loadStripe(stripePromiseKey);
  const handleClose = (args: any = null) => {
    setPlan(null);
    resetSubscriptionModal();
  };

  return (
    <Modal open={plan ? true : false} onClose={handleClose} sx={{ zIndex: 2000 }}>
      <Elements stripe={stripePromise}>
        <StripeContext plan={plan} handleClose={handleClose} />
      </Elements>
    </Modal>
  );
};

export default UpgradeSubscriptionModal;
